// Pieces shared by the manager dashboard (pages/dashboard/Dashboard.js) and
// the rep dashboard (pages/dashboard/rep/). Kept here so a stage colour or the
// rupee format means the same thing on both.
import { ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";

export const STAGE_COLORS = {
    'New Lead': 'bg-blue-500',
    'Contacted': 'bg-cyan-500',
    'Qualified': 'bg-purple-500',
    'Proposal': 'bg-orange-500',
    'Negotiation': 'bg-yellow-500',
    'Demo Fixed': 'bg-indigo-500',
    'Demo Done': 'bg-violet-500',
    'On Hold': 'bg-gray-400',
    'Unanswered': 'bg-slate-400',
    'No Show': 'bg-rose-400',
    'Closed Won': 'bg-green-500',
    'Closed Lost': 'bg-red-500',
    'Dis-Qualified': 'bg-red-400',
    'Nurture Lead': 'bg-teal-500',
};

export const formatINR = (value) => {
    const v = Number(value);
    if (!v) return '₹0';
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
    if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return `₹${v.toFixed(0)}`;
};

export const KpiCard = ({ title, value, icon: Icon, colorClass, sub, trend }) => (
    <div className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200/70 dark:border-gray-700/70 p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:shadow-[0_6px_16px_-6px_rgba(16,24,40,0.12)] hover:border-gray-300/80 dark:hover:border-gray-600 transition-all">
        <div className="flex items-center justify-between gap-2">
            <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400 truncate">
                {title}
            </p>
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", colorClass)}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
        <p className="text-[26px] leading-none font-semibold tracking-tight tabular-nums text-gray-900 dark:text-white mt-3">
            {value?.toLocaleString() ?? '—'}
        </p>
        {sub && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{sub}</p>
        )}
        {trend !== undefined && (
            <div className={cn(
                "inline-flex items-center gap-1 mt-2 text-xs font-medium",
                trend >= 0 ? 'text-emerald-600' : 'text-red-500'
            )}>
                {trend >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                <span>{Math.abs(trend)}% vs last month</span>
            </div>
        )}
    </div>
);

export const PipelineBar = ({ stage, count, total, right }) => {
    const pct = total ? Math.round((count / total) * 100) : 0;
    const color = STAGE_COLORS[stage] || 'bg-gray-400';
    return (
        <div className="flex items-center gap-2 py-1">
            <span className="text-xs text-gray-600 dark:text-gray-400 w-28 truncate">{stage}</span>
            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                <div className={cn("h-2 rounded-full transition-all", color)} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-8 text-right">{count}</span>
            {right}
        </div>
    );
};

// A chart colour from the theme's --chart-N tokens, so charts follow the brand
// and dark mode instead of hard-coded rgba values.
export const chartColor = (n, alpha = 1) => {
    if (typeof window === 'undefined') return `rgba(138,69,230,${alpha})`;
    const raw = getComputedStyle(document.documentElement).getPropertyValue(`--chart-${n}`).trim();
    return raw ? `hsl(${raw} / ${alpha})` : `rgba(138,69,230,${alpha})`;
};
